import React, { Component, Fragment } from 'react';
import { connect } from "react-redux";
import { IRootState } from "../../../store";
import DispatchProps from "../../../utils/DispatchProps";
import { debounce } from "throttle-debounce";
import TenantPackageSummary from '../../../models/TenantPackageSummary';
import { TextField, CommandBarButton, Dropdown, IDropdownOption, MessageBarType } from "office-ui-fabric-react";
import TenantPackageSummaryList from '../../TenantPackageSummaryList';
import ApiService from '../../ApiService';
import Actions from '../../../redux/Actions';
import SharePointPackage from '../../../models/SharePointPackage';
import { GetSharePointAppVersions } from '../../../utils/SharePointAppVersionUtils';
import PageTitle from "../../PageTitle";

interface TenantPackageManagementDashboardPageProps {
    apiService: ApiService;
}

interface TenantPackageManagementDashboardPageState {
    tenantPackageSummaries: TenantPackageSummary[];
    filterString?: string;
    filteredTenants: TenantPackageSummary[];
    tenantsSelected: Map<string, boolean>;
    selectedWorkloadOption: string | undefined;
    selectedPackageId: string | undefined;
    selectedPackageVersion: string | undefined | null;
    availablePackages: SharePointPackage[];
}

class TenantPackageManagementDashboardPage extends Component<TenantPackageManagementDashboardPageProps & DispatchProps, TenantPackageManagementDashboardPageState> {
    static displayName = TenantPackageManagementDashboardPage.name;

    private readonly debouncedFilterTenants: debounce<() => void>;

    constructor(props: TenantPackageManagementDashboardPageProps & DispatchProps) {
        super(props);

        this.debouncedFilterTenants = debounce(200, this.filterTenants);

        this.state = {
            tenantPackageSummaries: [],
            filterString: undefined,
            filteredTenants: [],
            tenantsSelected: new Map<string, boolean>(),
            selectedWorkloadOption: undefined,
            selectedPackageId: undefined,
            selectedPackageVersion: undefined,
            availablePackages: [],
        }
    }

    async componentDidMount() {
        const tenantPackageSummaries = await this.props.apiService.getTenantPackageSummariesAdmin();
        const packages = await this.props.apiService.getAvailableSharePointPackages();
        this.setState({ tenantPackageSummaries: tenantPackageSummaries, availablePackages: packages });
        this.debouncedFilterTenants();
    }

    async componentDidUpdate(prevProps: Readonly<TenantPackageManagementDashboardPageProps & DispatchProps>, prevState: any): Promise<void> {
        if (this.state.filterString !== prevState.filterString && this.state.tenantPackageSummaries) {
            this.debouncedFilterTenants();
        }
    }

    private async onStartWorkload() {
        var selectedTenants = this.getSelectedTenants();
        if (this.state.selectedWorkloadOption != null && selectedTenants.length > 0) {
            try {
                await this.props.apiService.startWorkloadForTenants(selectedTenants.map(tenant => tenant.tenantId as string), this.state.selectedWorkloadOption);
                this.props.dispatch(Actions.showMessageBar(`Succesfully started ${this.state.selectedWorkloadOption} for ${selectedTenants.length} Tenant(s)`, MessageBarType.success));
            }
            catch (e) {
                console.log(e);
                this.props.dispatch(Actions.showMessageBar(`Error starting ${this.state.selectedWorkloadOption} for one or more Tenants`, MessageBarType.error));
            }
        }
    }

    private getSelectedTenants(): TenantPackageSummary[] {
        var selectedTenants: TenantPackageSummary[] = [];
        this.state.filteredTenants.forEach(tenant => {
            if (tenant.tenantId && this.state.tenantsSelected.get(tenant.tenantId) === true) {
                selectedTenants.push(tenant);
            }
        });
        return selectedTenants;
    }

    private onSelectionChange() {
        var that = this;
        return (selection: Map<string, boolean>) => {
            that.setState({ tenantsSelected: selection });
        };
    }

    private getWorkLoadOptions(): IDropdownOption[] {
        const options = ["SetupSharePointApps", "UpdateObservedSharePointApps", "ExportCdbDesignExtensionProperties"];
        return options.map(option => {
            return {
                key: option,
                text: option
            }
        });
    }

    private getPackageNameOptions(): IDropdownOption[] {
        var availablePackages: SharePointPackage[] = [];
        this.state.availablePackages.forEach(availablePackage => {
            if (!availablePackages.find(a => a.id.toLocaleUpperCase() === availablePackage.id.toLocaleUpperCase())) {
                availablePackages.push(availablePackage);
            }
        });

        return availablePackages.map(availablePackage => {
            return {
                key: availablePackage.id.toLocaleUpperCase(),
                text: availablePackage.name ?? "Error: No Name Found"
            }
        });
    }

    private async onApplyPackage() {
        var selectedTenantIds = this.getSelectedTenants().map(tenant => tenant.tenantId as string);

        if (this.state.selectedPackageVersion && this.state.selectedPackageId && selectedTenantIds.length > 0) {
            var packageToApply: SharePointPackage = {
                id: this.state.selectedPackageId.toLocaleUpperCase() as string,
                version: this.state.selectedPackageVersion
            };

            try {
                await this.props.apiService.applySharePointAppToTenants(packageToApply, selectedTenantIds);
                this.props.dispatch(Actions.showMessageBar(`Succesfully applied selected packages for ${selectedTenantIds.length} Tenant(s)`, MessageBarType.success));

                const tenantPackageSummaries = await this.props.apiService.getTenantPackageSummariesAdmin();
                this.setState({ tenantPackageSummaries: tenantPackageSummaries });
                this.debouncedFilterTenants();
            }
            catch (e) {
                console.log(e);
                this.props.dispatch(Actions.showMessageBar(`Error applying selected packages for one or more Tenants`, MessageBarType.error));
            }
        }
    }

    private filterTenants(): void {
        if (!this.state.tenantPackageSummaries) {
            this.setState({
                filteredTenants: []
            });
            return;
        }

        var sorted = this.state.tenantPackageSummaries!.sort((a, b) => a.tenantName && b.tenantName ? a.tenantName?.localeCompare(b.tenantName) : 0);

        if (this.state.filterString === undefined || this.state.filterString === "") {
            this.setState({
                filteredTenants: sorted
            });
            return;
        }

        var filtered = sorted!.filter((tenant) => {
            if ((tenant.tenantName && tenant.tenantName.toLowerCase().indexOf(this.state.filterString!) !== -1) ||
                (tenant.tenantId && tenant.tenantId.toLowerCase().indexOf(this.state.filterString!) !== -1)) {
                return true;
            }
            else {
                return false;
            }
        });

        this.setState({ filteredTenants: filtered });
    }

    render() {
        return (
            <Fragment>
                
                <PageTitle title={"Tenants"}/>
                
                <div className="filter-controls p-top-1rem">
                    <div className="filter-controls__row">
                        <TextField className="filter-controls__input" autoFocus label="Tenant Name / Tenant ID" placeholder="Filter" onChange={
                            (event, newValue) => {
                                this.setState({
                                    filterString: newValue?.toLowerCase()
                                });
                            }
                        } />
                    </div>
                </div>

                <TenantPackageSummaryList
                    title={"Tenants"}
                    tenantPackageSummaries={this.state.filteredTenants}
                    enableRowLinks={true}
                    selectable={true}
                    onSelectionChange={this.onSelectionChange()}>
                    <div className="card__header__nested">
                        <div>
                            <Dropdown
                                className="workload-query-controls__input"
                                selectedKey={this.state.selectedWorkloadOption}
                                placeholder="Select workload to run against tenants"
                                onChange={(e, selected) => this.setState({ selectedWorkloadOption: selected?.key as string })}
                                options={this.getWorkLoadOptions()}
                                styles={{ dropdown: { width: 350 } }}
                            />
                            <CommandBarButton
                                iconProps={{ iconName: "Rocket" }}
                                disabled={!this.state.selectedWorkloadOption}
                                text={`Start Workload for ${this.getSelectedTenants().length} Tenant(s)`}
                                onClick={() => this.onStartWorkload()}
                            />
                        </div>
                        <div>
                            <Dropdown
                                className="workload-query-controls__input"
                                selectedKey={this.state.selectedPackageId ?? "Latest"}
                                placeholder="Select Package"
                                onChange={(e, selected) => {
                                    this.setState({ selectedPackageId: selected?.key as string, selectedPackageVersion: null });

                                }}
                                options={this.getPackageNameOptions()}
                                styles={{ dropdown: { width: 200 } }}
                            />
                            <Dropdown
                                className="workload-query-controls__input"
                                selectedKey={this.state.selectedPackageVersion}
                                placeholder="Select Version"
                                onChange={(e, selected) => {
                                    this.setState({ selectedPackageVersion: selected?.key as string })
                                }}
                                options={ GetSharePointAppVersions(this.state.selectedPackageId, this.state.availablePackages) }
                                styles={{ dropdown: { width: 200 } }}
                            />
                            <CommandBarButton
                                iconProps={{ iconName: "Save" }}
                                disabled={!this.state.selectedPackageVersion || !this.state.selectedPackageId}
                                text={`Apply Package to ${this.getSelectedTenants().length} Tenant(s)`}
                                onClick={() => this.onApplyPackage()}
                            />
                        </div>
                    </div>
                </TenantPackageSummaryList>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState): TenantPackageManagementDashboardPageProps => {
        return {
            apiService: state.auth.apiService,
        };
    }
)(TenantPackageManagementDashboardPage)